/**
 * WebSocketProvider — real vaqt bildirishnomalar
 *
 * Spec bo'lim 20: WebSocket orqali boshqa foydalanuvchi amallarini ko'rish.
 * Notification turlari: product CRUD, debt, order, sync.
 *
 * Gateway: packages/server/src/modules/notifications/app.gateway.ts
 * Reconnect logic: 3s → 6s → 12s (exponential backoff, max 30s).
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useAuthStore } from '@/app/stores/auth.store';
import { toast } from '@/shared/ui/toast';

interface WsNotification {
  type: string;
  message: string;
  data?: unknown;
}

interface WebSocketContextValue {
  isConnected: boolean;
  lastMessage: WsNotification | null;
}

const WS_DEFAULT: WebSocketContextValue = { isConnected: false, lastMessage: null };
const WebSocketContext = createContext<WebSocketContextValue>(WS_DEFAULT);

export function useWebSocket(): WebSocketContextValue {
  return useContext(WebSocketContext);
}

const MAX_RECONNECT_DELAY = 30_000;
const BASE_RECONNECT_DELAY = 3_000;

export function WebSocketProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WsNotification | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const attemptRef = useRef<number>(0);
  const token = useAuthStore((s) => s.token);

  const connect = useCallback(() => {
    if (token === null) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        attemptRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const notification = JSON.parse(String(event.data)) as WsNotification;
          setLastMessage(notification);

          if (notification.message) {
            toast.info(notification.message);
          }
        } catch {
          // Noto'g'ri format — e'tiborsiz qoldiramiz
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;

        // Reconnect with exponential backoff
        const delay = Math.min(BASE_RECONNECT_DELAY * 2 ** attemptRef.current, MAX_RECONNECT_DELAY);
        attemptRef.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // WebSocket yaratishda xato — reconnect
      const delay = Math.min(BASE_RECONNECT_DELAY * 2 ** attemptRef.current, MAX_RECONNECT_DELAY);
      attemptRef.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    }
  }, [token]);

  useEffect(() => {
    connect();

    return () => {
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return <WebSocketContext value={{ isConnected, lastMessage }}>{children}</WebSocketContext>;
}
